import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH26_QUESTIONS } from "../data/neetPhysicsCh26Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 26: Dual Nature of Radiation and Matter (NCERT Class 12).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Photoelectric Effect — Observations", "Einstein's Photoelectric Equation", "Photon Nature of Light", "de Broglie Matter Waves"];

const SECTION_COLORS = {
  "Photoelectric Effect — Observations": "#2563eb",
  "Einstein's Photoelectric Equation":   "#7c3aed",
  "Photon Nature of Light":              "#ea580c",
  "de Broglie Matter Waves":             "#0d9488",
};

const CONCEPTS = [
  // ── Photoelectric Effect — Observations ────────────────────────────────────
  { section: "Photoelectric Effect — Observations", title: "Discovery of the photoelectric effect — Hertz and Lenard",
    explanation: "In 1887, Heinrich Hertz noticed that sparking between two electrodes was enhanced when the emitter plate was illuminated by ultraviolet light, while producing and detecting electromagnetic waves. Hallwachs and Lenard then studied the effect systematically: when light of sufficiently short wavelength falls on a clean metal surface (especially alkali metals like Na, K, Cs), electrons are ejected from it. This ejection of electrons under illumination is called the photoelectric effect, and the ejected electrons are called photoelectrons.",
    example: `Setup: evacuated tube, emitter plate C + collector plate A
UV/short-wavelength light -> falls on C -> electrons ejected
Electrons reach A -> photoelectric current registered on a
microammeter, even with no direct electrical excitation of C

Observation: alkali metals respond even to visible light;
most other metals need ultraviolet light to show the effect` },
  { section: "Photoelectric Effect — Observations", title: "Effect of intensity of incident light on photoelectric current",
    explanation: "With the frequency of incident light and the retarding potential held fixed, the photoelectric current (photoelectrons emitted per second) is directly proportional to the intensity of the incident light -- more intense light means more photoelectrons detected per second, up to the saturation current, where every photoelectron emitted is collected.",
    example: `At fixed frequency v and zero retarding potential:
Photoelectric current (i)  ∝  Intensity (I) of incident light

Doubling the intensity -> photoelectric current doubles
(saturation current is reached once every photoelectron
 emitted per second is collected by the plate)` },
  { section: "Photoelectric Effect — Observations", title: "Stopping potential and retarding potential",
    explanation: "Making the collector plate negative relative to the emitter (a retarding potential) opposes the photoelectrons; as this retarding potential is increased, the current falls. The minimum negative potential at which the photoelectric current becomes exactly zero is called the stopping potential (or cut-off potential) V0. It measures the maximum kinetic energy of the emitted photoelectrons: eV0 = KEmax.",
    example: `eV0 = KE(max) = (1/2) m v(max)^2

If a metal's photoelectrons are just stopped by V0 = 0.35 V:
KE(max) = eV0 = (1.6x10^-19 C)(0.35 V) = 5.6x10^-20 J` },
  { section: "Photoelectric Effect — Observations", title: "Effect of frequency — threshold frequency",
    explanation: "For a given metal, the stopping potential (and hence KEmax) increases with the frequency of incident light and is completely independent of intensity. Below a certain minimum frequency v0, called the threshold frequency, NO photoelectrons are emitted no matter how intense the light is or how long it shines; above v0, emission begins instantaneously.",
    example: `v < v0  -> no photoelectric emission at all
              (regardless of intensity or duration of exposure)
v > v0  -> emission is instantaneous; stopping potential grows
           linearly with v, independent of intensity` },
  { section: "Photoelectric Effect — Observations", title: "Work function of a metal",
    explanation: "The work function φ0 is the minimum energy needed to just eject an electron from a metal surface, overcoming the attractive forces that hold it there. It is typically 2-5 eV for common metals, and is related to the threshold frequency by φ0 = h.v0 (and to the threshold wavelength by λ0 = hc/φ0). Alkali metals have a low work function (~2 eV) and respond to visible light; most other metals need ultraviolet light.",
    example: `φ0 = h.v0   =>   v0 = φ0 / h

Caesium: φ0 = 2.14 eV = 2.14 x 1.6x10^-19 J = 3.42x10^-19 J
v0 = 3.42x10^-19 / 6.63x10^-34 ≈ 5.16x10^14 Hz

Threshold wavelength: λ0 = c/v0 = (3x10^8)/(5.16x10^14)
                          ≈ 5.81x10^-7 m = 581 nm (green light)` },

  // ── Einstein's Photoelectric Equation ───────────────────────────────────────
  { section: "Einstein's Photoelectric Equation", title: "Einstein's photoelectric equation",
    explanation: "Einstein (1905) explained the photoelectric effect by treating light as a stream of photons, each of energy h.v. When a photon strikes a metal surface, it is completely absorbed by ONE electron (no sharing between electrons); part of this energy (φ0) frees the electron from the surface, and the remainder appears as the electron's kinetic energy. This gives Einstein's photoelectric equation: KE(max) = h.v - φ0, and eV0 = KE(max).",
    example: `KE(max) = hv - φ0

Caesium (φ0 = 2.14 eV), light of frequency v = 6x10^14 Hz:
hv = (6.63x10^-34)(6x10^14) = 3.978x10^-19 J ≈ 2.49 eV

KE(max) = 2.49 - 2.14 = 0.35 eV
Stopping potential V0 = KE(max)/e = 0.35 V` },
  { section: "Einstein's Photoelectric Equation", title: "Explaining the threshold frequency and instantaneous emission",
    explanation: "The photon picture explains why a threshold frequency exists: since each photoelectron is produced by ONE photon being absorbed by ONE electron in a single event, emission happens only if that single photon's energy h.v is at least φ0 -- energy from separate photons can never add up across multiple collisions. This one-photon, one-electron picture also explains why emission is essentially instantaneous (no measurable time lag even at very low intensities), in sharp contrast to the classical wave picture, which wrongly predicted electrons would need time to gradually accumulate enough energy.",
    example: `Wave theory (WRONG prediction): at low intensity, an electron
should need time to "soak up" enough energy -> measurable
time lag before emission begins

Photon theory (OBSERVED): each electron absorbs ONE photon in
a single collision -> either hv >= φ0 (instant emission) or
hv < φ0 (no emission, ever, regardless of intensity or time)` },
  { section: "Einstein's Photoelectric Equation", title: "Explaining the effect of intensity on photoelectric current",
    explanation: "The intensity of light equals the number of photons incident per unit area per unit time (n) multiplied by the energy per photon (h.v). At a fixed frequency, more intense light means a larger photon flux n, so more photoelectrons are ejected per second and the photoelectric current is larger -- but KE(max) of each individual photoelectron depends only on h.v - φ0, and NOT on intensity, since each emission event still involves just one photon and one electron.",
    example: `Intensity I = n . (hv)   (n = photons per unit area per unit time)

Doubling I at the same v  ->  n doubles  ->  current doubles
KE(max) = hv - φ0  -- unchanged, since it depends only on v` },
  { section: "Einstein's Photoelectric Equation", title: "Effect of frequency on stopping potential — linear relationship",
    explanation: "From Einstein's equation, eV0 = h.v - φ0, so V0 = (h/e).v - (φ0/e). This predicts that the stopping potential increases LINEARLY with frequency, with slope h/e, completely independent of intensity -- and this matches experiment exactly, a major triumph for the photon theory over the classical wave theory. The line's x-intercept gives the threshold frequency v0.",
    example: `V0 = (h/e).v - (φ0/e)     [a straight line: y = mx + c]

For caesium (φ0 = 2.14 eV), at v = 8x10^14 Hz:
hv = (6.63x10^-34)(8x10^14) = 5.30x10^-19 J ≈ 3.32 eV
V0 = 3.32 - 2.14 = 1.18 V` },
  { section: "Einstein's Photoelectric Equation", title: "Graph of stopping potential vs frequency — determining h",
    explanation: "Plotting the stopping potential V0 (y-axis) against frequency v (x-axis) for a given metal gives a straight line. The SLOPE of this line equals h/e -- the same for every metal, since h is a universal constant -- and the x-intercept gives the threshold frequency v0 (so φ0 = h.v0). Millikan's precise measurement of this slope in 1916 gave an accurate value of Planck's constant, providing strong experimental confirmation of Einstein's photon theory.",
    example: `slope = ΔV0 / Δv = h/e     (same for every metal -- universal)

If ΔV0 = 2.07 V over Δv = 5x10^14 Hz:
h = e x slope = (1.6x10^-19)(2.07 / 5x10^14)
  = (1.6x10^-19)(4.14x10^-15) ≈ 6.63x10^-34 J.s   (matches h!)` },

  // ── Photon Nature of Light ───────────────────────────────────────────────────
  { section: "Photon Nature of Light", title: "Photon energy, E = hν = hc/λ",
    explanation: "In the photon picture, light consists of discrete packets (quanta) of energy called photons. Each photon of frequency v (or wavelength λ) carries energy E = h.v = hc/λ, regardless of the light's intensity -- intensity governs only the NUMBER of photons delivered per second, not the energy carried by each individual photon.",
    example: `E = hc / λ

For λ = 663 nm (red light):
E = (6.63x10^-34)(3x10^8) / (663x10^-9)
  = (1.989x10^-25) / (6.63x10^-7)
  = 3.0x10^-19 J  ≈ 1.875 eV` },
  { section: "Photon Nature of Light", title: "Photon momentum, p = h/λ = E/c",
    explanation: "Even though a photon has zero rest mass, it carries momentum -- a purely relativistic result: p = E/c = h.v/c = h/λ. This momentum is what lets light exert a force (radiation pressure) on a surface, and is central to explaining phenomena such as the Compton effect.",
    example: `p = h/λ = E/c

For λ = 663 nm:
p = (6.63x10^-34) / (663x10^-9) = 1.0x10^-27 kg.m/s

Check via E/c: p = (3.0x10^-19 J) / (3x10^8 m/s)
             = 1.0x10^-27 kg.m/s  ✓ (matches)` },
  { section: "Photon Nature of Light", title: "Characteristic properties of a photon",
    explanation: "Key properties of a photon: it travels at speed c in vacuum, regardless of the motion of its source; its rest mass is exactly zero (it cannot exist at rest); its energy and momentum depend only on frequency (E = h.v, p = h.v/c), not on the light's intensity; it is electrically neutral, so it is undeflected by electric or magnetic fields; and in collisions with particles (as in the photoelectric effect or Compton scattering), energy and momentum are conserved just as for ordinary particles.",
    example: `Photon properties:
1. Travels at speed c (vacuum), regardless of source's motion
2. Rest mass = 0 (cannot be brought to rest)
3. Energy E = hv, momentum p = hv/c -- depend on v, not intensity
4. Electrically neutral -- undeflected by E and B fields
5. Energy & momentum conserved in photon-particle collisions` },
  { section: "Photon Nature of Light", title: "Photon flux — number of photons emitted per second",
    explanation: "For a light source of power P (energy emitted per second) and wavelength λ, if every photon has the same energy E = hc/λ, then the number of photons emitted per second (the photon flux) is n = P/E = P.λ/(hc). This lets us connect a source's everyday power rating to the microscopic photon picture.",
    example: `n = P / E = Pλ / (hc)

100 W sodium lamp, λ = 589 nm:
E per photon = hc/λ = (6.63x10^-34)(3x10^8)/(589x10^-9)
             ≈ 3.38x10^-19 J

n = 100 / 3.38x10^-19 ≈ 3.0x10^20 photons emitted per second` },
  { section: "Photon Nature of Light", title: "Radiation pressure",
    explanation: "Since photons carry momentum, light incident on a surface exerts a force -- radiation pressure. For light of intensity I incident normally on a perfectly absorbing (black) surface, the pressure P = I/c (momentum delivered per second per unit area equals energy delivered per second per unit area, divided by c). On a perfectly reflecting surface, the pressure is DOUBLED, P = 2I/c, since each photon's momentum reverses direction, transferring twice the momentum.",
    example: `P(absorbing) = I/c          P(reflecting) = 2I/c

Sunlight intensity I ≈ 1400 W/m^2 on an absorbing surface:
P = 1400 / (3x10^8) ≈ 4.7x10^-6 Pa

(extremely small, but real -- used in solar-sail spacecraft
 propulsion design, and explains the direction of comet tails)` },

  // ── de Broglie Matter Waves ──────────────────────────────────────────────────
  { section: "de Broglie Matter Waves", title: "de Broglie hypothesis — wave nature of matter",
    explanation: "In 1924, Louis de Broglie proposed that just as light (traditionally a wave) shows particle-like behaviour (photons), matter (traditionally particles) should also show wave-like behaviour. He suggested that every moving particle of momentum p has an associated matter wave of wavelength λ = h/p -- a symmetry between radiation and matter. This wave nature is significant only for very small masses/momenta (the atomic scale) and is utterly negligible for everyday macroscopic objects.",
    example: `Radiation: particle (photon) properties emerge from a wave:
             E = hv,   p = h/λ

Matter:    wave properties emerge from a particle:
             λ = h/p = h/(mv)

de Broglie wavelength exists for ALL moving matter -- electrons,
protons, atoms, even cricket balls -- but is only observable
for very light, fast-moving (atomic-scale) particles` },
  { section: "de Broglie Matter Waves", title: "de Broglie wavelength and its relation to kinetic energy",
    explanation: "Starting from λ = h/p, if a particle's (non-relativistic) kinetic energy is K = p^2/(2m), then its momentum is p = sqrt(2mK), so the de Broglie wavelength can be written as λ = h/sqrt(2mK). This form directly connects a particle's kinetic energy to its associated wavelength.",
    example: `λ = h / p = h / √(2mK)      (K = kinetic energy, non-relativistic)

Thermal neutron, K = 0.025 eV (room temperature), m = 1.675x10^-27 kg:
K = 0.025 x 1.6x10^-19 = 4.0x10^-21 J
p = √(2 x 1.675x10^-27 x 4.0x10^-21) ≈ 3.67x10^-24 kg.m/s
λ = 6.63x10^-34 / 3.67x10^-24 ≈ 1.81x10^-10 m = 1.81 Å

(comparable to interatomic spacing -- basis of neutron diffraction)` },
  { section: "de Broglie Matter Waves", title: "de Broglie wavelength of an electron accelerated through a potential difference",
    explanation: "An electron starting from rest and accelerated through a potential difference V gains kinetic energy K = eV = (1/2)mv^2, so its momentum is p = mv = sqrt(2meV), giving de Broglie wavelength λ = h/sqrt(2meV). Substituting the values of h, m and e gives the widely used practical formula λ (in angstrom) = 12.27/sqrt(V), with V in volts.",
    example: `λ = h / √(2meV) = 12.27 / √V   Å   (V in volts)

Electron accelerated through V = 150 V:
λ = 12.27 / √150 = 12.27 / 12.25 ≈ 1.00 Å

(comparable to X-ray wavelengths and interatomic spacing --
 used in electron diffraction studies of crystals)` },
  { section: "de Broglie Matter Waves", title: "Why macroscopic objects don't show observable wave nature",
    explanation: "Because Planck's constant h is extremely small (6.63x10^-34 J.s), even objects with a tiny mass moving at ordinary speeds have momentum large enough to make λ = h/p immeasurably small -- far smaller than any observable aperture -- so no diffraction or interference effects are ever seen. Wave nature becomes significant only when λ is comparable to the size of the system probing it, such as the atomic spacing (~10^-10 m) relevant to electrons.",
    example: `λ = h/p

Cricket ball: m = 0.15 kg, v = 30 m/s
p = 0.15 x 30 = 4.5 kg.m/s
λ = 6.63x10^-34 / 4.5 ≈ 1.5x10^-34 m

(compare: an electron's de Broglie wavelength is ~10^-10 m --
about 24 orders of magnitude larger -- so only microscopic
particles show observable wave/diffraction effects)` },
  { section: "de Broglie Matter Waves", title: "Davisson-Germer experiment",
    explanation: "Performed in 1927, the Davisson-Germer experiment gave the first direct experimental confirmation of de Broglie's matter-wave hypothesis. A beam of electrons, accelerated through a known potential difference, was directed at a nickel crystal; the intensity of scattered electrons showed a pronounced maximum at a scattering angle of 50 degrees (for 54 V electrons) -- exactly analogous to Bragg diffraction of X-rays from a crystal lattice, confirming that electrons exhibit wave behaviour. The wavelength obtained from the diffraction pattern matched the de Broglie wavelength predicted from λ = 12.27/sqrt(V).",
    example: `λ (de Broglie) = 12.27/√V = 12.27/√54 ≈ 1.67 Å

Davisson-Germer (1927): 54 V electron beam on a nickel crystal,
strong scattering maximum observed at 50 degrees -> using
Bragg's law, the experimental wavelength ≈ 1.65 Å

Excellent agreement with the de Broglie prediction (1.67 Å) --
first direct experimental confirmation of matter waves` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh26Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh26Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh26Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH26_QUESTIONS : NEET_PHYSICS_CH26_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 26</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Dual Nature of Radiation and Matter</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH26_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH26_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
